import { defineStore } from 'pinia'

import {
  contactsRowTemplate,
  newBusinessRowTemplate,
  ownerRelatedEmployeesRowTemplate,
  ownersTemplate,
  relatedOwnersRowTemplate,
  spouseOwnedBusinessesRowTemplate,
} from '~/components/groupedRelationshipBoxTemplates/rowTemplates'

import env from '~/libs/env'
import get from 'lodash/get'
import merge from 'lodash/merge.js'
import { useRootStore } from '~/store/root'
import { useBorrowerStore } from '~/store/borrower'

export const useContactsStore = defineStore('contacts', () => {
  const rootStore = useRootStore()
  const borrowerStore = useBorrowerStore()
  const nuxtApp = useNuxtApp()
  const { $axios } = nuxtApp
/*
  ███████ ████████  █████  ████████ ███████
  ██         ██    ██   ██    ██    ██
  ███████    ██    ███████    ██    █████
       ██    ██    ██   ██    ██    ██
  ███████    ██    ██   ██    ██    ███████
  STATE
*/

  const relatedOwners = ref([])
  const relatedOwnersUnsavedAnswers = ref({})
  const ownerRelatedEmployees = ref([])
  const ownersLoaded = ref(false)
  const owners = ref([])
  const unmodifiedOwners = ref([])
  const primaryContactOwner = ref({})
  const additionalBusinesses = ref([])
  const ercContacts = ref([])
  const ercContactsUnsavedAnswers = ref({})
  const ownersUnsavedAnswers = ref({})
  const ercSpouseOwnedBusinesses = ref([])
  const ercSpouseOwnedBusinessesUnsavedAnswers = ref({})
  const _contactsRowTemplate = ref(contactsRowTemplate)
  const _ownersTemplate =ref(ownersTemplate)
  const _newBusinessRowTemplate = ref(newBusinessRowTemplate)
  const _ownerRelatedEmployeesRowTemplate = ref(ownerRelatedEmployeesRowTemplate)
  const _relatedOwnersRowTemplate = ref(relatedOwnersRowTemplate)
  const _spouseOwnedBusinessesRowTemplate = ref(spouseOwnedBusinessesRowTemplate)
  const unsavedAdditionalBusinesses = ref([])

 /*
   ██████  ███████ ████████ ████████ ███████ ██████  ███████
  ██       ██         ██       ██    ██      ██   ██ ██
  ██   ███ █████      ██       ██    █████   ██████  ███████
  ██    ██ ██         ██       ██    ██      ██   ██      ██
   ██████  ███████    ██       ██    ███████ ██   ██ ███████
  GETTERS
*/

  const hasRelatedOwners = computed(() => {
    return relatedOwners.value.length > 0
  })

  const hasRelatedEmployees = computed(() => {
    return ownerRelatedEmployees.value.length > 0
  })

  const hasSpouseOwnedBusinesses = computed(() =>  {
    return ercSpouseOwnedBusinesses.value.length > 0
  })

  const ercNewBusinessRows =  computed(() => {
    return _newBusinessRowTemplate.value
  })

  const ownersOptions =  computed(() => {
    const _ercContacts = ercContacts.value

    return _ercContacts.map((contact) => {
      return {
        value: contact.contactId,
        label: contact.first + ' ' + contact.last
      }
    })
  })

  /**
     * This returns something similar to owners options,
     * but will return the parent contact ids if available
     * @returns Object[]
     */

  const relatedOwnersOptions = computed(() => {
    const _ercContacts = ercContacts.value

    return _ercContacts.map((contact) => {
      return {
        value: contact.parentContactId || contact.contactId,
        label: contact.first + ' ' + contact.last
      }
    })
  })

  const spouseOwnedBusinessesRowTemplateWithOptions = computed(() => {
    const options = ownersOptions.value
    const questions = _spouseOwnedBusinessesRowTemplate.value.questions.map(
      (question) => {
        if (question.alias === 'contactsFamily.ownerContactId') {
          question.options = options
        }
        return question
      }
    )
    return {
      ..._spouseOwnedBusinessesRowTemplate.value,
      questions
    }
  })

  const ercSpouseOwnedBusinessRows = computed(() => {
    return ercSpouseOwnedBusinesses.value.map((spouseOwnedBusiness) => {
      const spouseOwnedRowTemplateQuestionsLocal =
        _spouseOwnedBusinessesRowTemplate.value.questions.map((question) => {
          const newQuestion = { ...question }
          if (newQuestion.alias == 'borrower.name') {
            newQuestion.id = spouseOwnedBusiness.businessId
            newQuestion.value = spouseOwnedBusiness.name
          }
          if (newQuestion.alias == 'contacts.spouseName') {
            newQuestion.id = spouseOwnedBusiness.businessId
            newQuestion.value = spouseOwnedBusiness.spouseFullName
          }
          if (newQuestion.alias == 'ercContactsOwnership.percentOwnership') {
            newQuestion.id = spouseOwnedBusiness.spouseOwnershipId
            newQuestion.value = spouseOwnedBusiness.percentOwnership
          }
          if (newQuestion.alias == 'contactsFamily.ownerContactId') {
            newQuestion.id = spouseOwnedBusiness.marriedToRowId
            newQuestion.value = spouseOwnedBusiness.marriedToId
            newQuestion.options = ownersOptions.value
          }
          return newQuestion
        })

      return {
        ..._spouseOwnedBusinessesRowTemplate.value,
        storeRow:
          spouseOwnedBusiness.businessId.toString().substring(0, 3) !== 'new',
        rowId: spouseOwnedBusiness.businessId,
        questions: spouseOwnedRowTemplateQuestionsLocal
      }
    })
  })

  const ercContactsRows = computed(() => {
    return ercContacts.value.map((contact) => {
      const contactsRowTemplateQuestionsLocal = _contactsRowTemplate.value.questions.map(
        (question) => {
          const newQuestion = { ...question }
          if (newQuestion.alias == 'contact.first') {
            newQuestion.value = contact.first
          }
          if (newQuestion.alias == 'contact.last') {
            newQuestion.value = contact.last
          }
          if (newQuestion.alias == 'ercContactsOwnership.percentOwnership') {
            newQuestion.value = contact.percentOwnership
          }
          return newQuestion
        }
      )

      return {
        ..._contactsRowTemplate.value,
        isPrimary: contact.isPrimary,
        storeRow: contact.contactId.toString().substring(0, 3) !== 'new',
        rowId: contact.contactId,
        questions: contactsRowTemplateQuestionsLocal
      }
    })
  })

  const ownerRows = computed(() => {
    return owners.value.map((owner) => {
      const ownersTemplateQuestions = ownersTemplate.questions.map(
        (question) => {
          const newQuestion = { ...question }
          if (newQuestion.alias == 'owners.first') {
            newQuestion.value = owner.first
          }
          if (newQuestion.alias == 'owners.last') {
            newQuestion.value = owner.last
          }
          if (newQuestion.alias == 'owners.percentOwnership') {
            newQuestion.value = owner.percentOwnership
          }
          if (newQuestion.alias == 'owners.email') {
            newQuestion.value = owner.email
          }
          return newQuestion
        }
      )

      return {
        ...ownersTemplate,
        // If id isn't 'new' it's a preloaded owner. Setting both of these
        // to same value for better readability (isPrimary check).
        isPrimary: !!owner.isPrimary,
        storeRow: owner.id.toString().substring(0, 3) !== 'new',
        rowId: owner.id,
        questions: ownersTemplateQuestions
      }
    })
  })

  const savedAndUnsavedOwners = computed(() => {
    const allOwners = []
    for (let owner of owners.value) {
      const changes = {}
      if (ownersUnsavedAnswers.value.hasOwnProperty(owner.id)) {
        const dirtyFields = Object.entries(ownersUnsavedAnswers.value[owner.id])
        for (const [key, value] of dirtyFields) {
          const newKey = key.replace('owners.', '')
          changes[newKey] = value
        }
      }

      allOwners.push({ ...owner, ...changes })
    }
    return allOwners;
  })

  const ownerRelatedEmployeesRowTemplateWithOptions = computed(() => {
    const options = ownersOptions.value
    const questions = _ownerRelatedEmployeesRowTemplate.value.questions.map(
      (question) => {
        if (question.alias === 'ownerRelatedEmployees.owner1') {
          question.options = options
        }
        return question
      }
    )
    return {
      ..._ownerRelatedEmployeesRowTemplate.value,
      questions
    }
  })

  const relatedEmployeesRows = computed(() => {
    return ownerRelatedEmployees.value.map((contactsFamily) => {
      const ownerRelatedEmployeesRowTemplateQuestionsLocal =
        _ownerRelatedEmployeesRowTemplate.value.questions.map((question) => {
          const newEmployee = { ...question }
          if (newEmployee.alias == 'ownerRelatedEmployees.employee1') {
            newEmployee.value = contactsFamily.employeeName
          }
          if (newEmployee.alias == 'ownerRelatedEmployees.relationship') {
            newEmployee.value = contactsFamily.relationship
          }
          if (newEmployee.alias == 'ownerRelatedEmployees.owner1') {
            newEmployee.value = contactsFamily.ownerContactId
            newEmployee.options = ownersOptions.value
          }
          return newEmployee

        })

      return {
        ..._ownerRelatedEmployeesRowTemplate.value,
        storeRow: contactsFamily.id.toString().substring(0, 3) !== 'new',
        rowId: contactsFamily.id,
        questions: [...ownerRelatedEmployeesRowTemplateQuestionsLocal]
      }
    })
  })

  const relatedOwnersRowTemplateWithOptions = computed(() => {
    const options = ownersOptions.value
    const questions = _relatedOwnersRowTemplate.value.questions.map((question) => {
      const aliasList = ['relatedOwners.owner2', 'relatedOwners.owner1']
      if (aliasList.indexOf(question.alias) >= 0) {
        question.options = options
      }
      return question
    })
    return {
      ..._relatedOwnersRowTemplate.value,
      questions
    }
  })

  const relatedOwnersRows = computed(() => {
    return relatedOwners.value.map((contactsFamily) => {

      const relatedOwnersRowTemplateQuestionsLocal =
        _relatedOwnersRowTemplate.value.questions.map((question) => {
          const newOwner = { ...question }
          if (newOwner.alias == 'relatedOwners.owner1') {
            newOwner.id = contactsFamily.id
            newOwner.value = contactsFamily.ownerContactId
            newOwner.options = ownersOptions.value
          }
          if (newOwner.alias == 'relatedOwners.relationship') {
            newOwner.value = contactsFamily.relationship
          }
          if (newOwner.alias == 'relatedOwners.owner2') {
            newOwner.id = contactsFamily.id
            newOwner.value = contactsFamily.relatedToContactId
            newOwner.options = ownersOptions.value
          }
          return newOwner
        })

      return {
        ..._relatedOwnersRowTemplate.value,
        storeRow: contactsFamily.id.toString().substring(0, 3) !== 'new',
        rowId: contactsFamily.id,
        questions: [...relatedOwnersRowTemplateQuestionsLocal]
      }
    })
  })

  const ownersRows = computed(() => {
    return owners.value.map((owner) => {
      const ownersTemplateQuestions = ownersTemplate.questions.map(
        (question) => {
          const newQuestion = { ...question }
          if (newQuestion.alias == 'owners.first') {
            newQuestion.value = owner.first
          }
          if (newQuestion.alias == 'owners.last') {
            newQuestion.value = owner.last
          }
          if (newQuestion.alias == 'owners.percentOwnership') {
            newQuestion.value = owner.percentOwnership
          }
          if (newQuestion.alias == 'owners.email') {
            newQuestion.value = owner.email
          }
          return newQuestion
        }
      )

      return {
        ...ownersTemplate,
        // If id isn't 'new' it's a preloaded owner. Setting both of these
        // to same value for better readability (isPrimary check).
        isPrimary: !!owner.isPrimary,
        storeRow: owner.id.toString().substring(0, 3) !== 'new',
        rowId: owner.id,
        questions: ownersTemplateQuestions
      }
    })
  })


  /*
   █████   ██████ ████████ ██  ██████  ███    ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ████   ██ ██
  ███████ ██         ██    ██ ██    ██ ██ ██  ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ██  ██ ██      ██
  ██   ██  ██████    ██    ██  ██████  ██   ████ ███████
  ACTIONS
  ! - - Actions calling other actions in the same store must use `this.actionName(...)`
  ! - - If we do not use `this.actionName` it will not be properly mockable in tests.
  ! - - Computeds and refs will work fine, and should be called directly though.
*/

  function updateUnsavedOwner(index, prop, value) {
    ownersUnsavedAnswers.value = {...ownersUnsavedAnswers.value}
    ownersUnsavedAnswers.value[index][prop] = value
    ownersUnsavedAnswers.value[index][prop] = value
  }

  function removeOwner(index) {
    const copy = [...owners.value]
    copy.splice(index,1)
    owners.value = copy
  }

  async function getOwnersByBorrowerId(borrowerId) {
    const url = `${env(
      'apiUrl'
    )}/borrower/${borrowerId}/contacts`
    const response = await $axios.get(url).catch((e) => {
      console.log(e)
    })
    const allOwners = get(response, 'data.data')
    primaryContactOwner.value = allOwners.find(x => x.isPrimary)
    owners.value = allOwners
      // TODO: confirm showing primary with Tyler B. Email is disabled.
      // .filter(x => !x.isPrimary)
      .map(contact => {
        if (contact.isPrimary) {
          // See if we have the ownership percent in borrower values.
          const bvOwnership = get(borrowerStore, 'borrowerValues.percentOwnership.value')
          if (bvOwnership != null) {
            contact.percentOwnership = bvOwnership
          }
        }
        const { id, first, last, email, percentOwnership, isPrimary } = contact
        return { id, first, last, email, percentOwnership, isPrimary}
      })
    unmodifiedOwners.value = [...owners.value]
    ownersLoaded.value = true
  }

  async function postOwners(borrowerId) {
    const modifiedOwners = []

    // Go through each owner that was entered in the UI form
    // when page was submitted. Apply the updates we tracked
    // in 'ownersUnsavedAnswers', and mark each as contract signers
    for (let owner of owners.value) {
      const changes = {}
      if (ownersUnsavedAnswers.value.hasOwnProperty(owner.id)) {
        const dirtyFields = Object.entries(ownersUnsavedAnswers.value[owner.id])
        for (const [key, value] of dirtyFields) {
          const newKey = key.replace('owners.', '')
          changes[newKey] = value
        }
      }
      modifiedOwners.push({...owner, ...changes, contractSigner: true, borrowerId: borrowerId })
    }

    // For any owner retrieved from backend, check to see if they are still included in the form.
    // If they aren't (i.e. they were deleted from the form or their email was modified) mark them
    // contractSigner=false but don't update any other info.
    for (let owner of unmodifiedOwners.value) {
      const overwrite = modifiedOwners.find(x => x.email?.toLowerCase() == owner.email?.toLowerCase())
      if (!overwrite) {
        owner['contractSigner'] = false,
        owner['borrowerId'] = borrowerId
        modifiedOwners.push(owner)
      }
    }

    const url = `${env(
      'apiUrl'
    )}/contact/upsert-contract-signers`
    const response = await $axios.post(url, modifiedOwners)
  }

  async function getRelatedOwnersByBorrowerId(borrowerId) {
    const url = `${env(
      'apiUrl'
    )}/contacts-family/related-owners/borrower/${borrowerId}`
    const response = await $axios.get(url).catch((e) => {
      console.log(e)
    })
    const _relatedOwners = get(response, 'data.data')

    if (Array.isArray(_relatedOwners) && _relatedOwners.length !== 0) {
      relatedOwners.value = _relatedOwners
    } else {
      relatedOwners.value = [
        {
          ...relatedOwnersRowTemplateWithOptions.value,
          id: 'new-' + Date.now()
        }
      ]
    }
  }

  async function getErcSpouseOwnedBusinesses(borrowerId) {

  const currentUser = get(rootStore.authUser, 'id')
    ? rootStore.authUser
    : await rootStore.getCurrentUser({
        bypassRedirect: true
      })
  if (!currentUser || typeof currentUser.id === 'undefined') return
  const url = `${env('apiUrl')}/user/${get(
    currentUser,
    'id'
  )}/borrower/${borrowerId}/borrowers/spouse-owned`
  const response = await $axios.get(url).catch(function (error) {
    console.log(error)
    // Do Error Handling
  })
  const _ercSpouseOwnedBusinesses = get(response, 'data')
  if (
    Array.isArray(_ercSpouseOwnedBusinesses) &&
    _ercSpouseOwnedBusinesses.length > 0
  ) {
    ercSpouseOwnedBusinesses.value = _ercSpouseOwnedBusinesses
  } else {
      const id = 'new-' + Date.now()
      ercSpouseOwnedBusinesses.value = [
        {
          id: id,
          businessId: id,
          ...spouseOwnedBusinessesRowTemplateWithOptions.value
        }
      ]
    }
  }

  async function updateErcSpouseOwnedBusinesses(data, borrowerId) {
    return $axios
      .put(`${env('apiUrl')}/borrower/${borrowerId}/spouse-business/`, data)
      .catch((e) => {
        console.log(err)
      })
  }

  async function getOwnerRelatedEmployeesByBorrowerId(borrowerId) {
    const url = `${env(
      'apiUrl'
    )}/contacts-family/related-employees/borrower/${borrowerId}`
    const response = await $axios.get(url).catch((e) => {
      console.log(err)
    })
    const _ownerRelatedEmployees = get(response, 'data.data')
    if (
      Array.isArray(_ownerRelatedEmployees) &&
      _ownerRelatedEmployees.length > 0
    ) {
      ownerRelatedEmployees.value = _ownerRelatedEmployees
    } else {
      // There are no borrowers
      ownerRelatedEmployees.value = [
        {
          ...ownerRelatedEmployeesRowTemplateWithOptions.value,
          id: 'new-' + Date.now()
        }
      ]

    }
  }

  async function createRelatedOwnerByBorrowerId(borrowerId) {
    const response = $axios
      .post(
        env('apiUrl') +
          `/contacts-family/related-owners/borrower/${borrowerId}`,
        data
      )
      .then((response) => {
        const index = relatedOwners.value.findIndex(
          (owner) => owner.ownerContactId === data.ownerContactId
        )
        if (index) {
          merge(relatedOwners.value[index], response.data.data)
        }
      })
      .catch((err) => {
        console.log(err)
      })
    return response
  }

  async function createOwnerRelatedEmployeeByBorrowerId(borrowerId) {
    const response = $axios
      .post(
        env('apiUrl') +
          `/contacts-family/related-employees/borrower/${borrowerId}`,
        data
      )
      .then((response) => {
        const index = ownerRelatedEmployees.value.findIndex(
          (owner) => owner.ownerContactId === data.ownerContactId
        )
        if (index) {
          merge(ownerRelatedEmployees.value[index], response.data.data)
        }
      })
      .catch((err) => {
        console.log(err)
      })
    return response
  }

  async function getErcContactsByBorrowerId(borrowerId) {
    const url = `${env('apiUrl')}/borrower/${borrowerId}/contacts/erc`
    const response = await $axios.get(url).catch((e) => {
      console.error(e)
    })
    const _ercContacts = get(response, 'data.data')

    if (Array.isArray(_ercContacts) && _ercContacts.length > 0) {
      ercContacts.value = _ercContacts
    } else {
      // There are no borrowers
      ercContacts.value = [
        {
          ..._contactsRowTemplate.value,
          id: 'new-' + Date.now()
        }
      ]
    }
  }

  async function createErcContact(data) {
    return $axios
      .post(env('apiUrl') + `/contact/erc-contact`, data)
      .then((response) => {
        const index = ercContacts.value.findIndex(
          (contact) => contact.contactId === data.contactId
        )
        if (index) {
          merge(ercContacts.value[index], response.data.data)
        }
        return response
      })
      .catch((err) => {
        console.log(err)
      })
  }

  async function updateErcContact(data, contactId) {
    return $axios
      .put(env('apiUrl') + `/contact/${contactId}/erc-contact`, data)
      .then((response) => {
        const index = ercContacts.value.findIndex(
          (contact) => contact.contactId === contactId
        )
        if (index) {
          merge(ercContacts.value[index], response.data.data)
        }
        return response
      })
      .catch((err) => {
        console.log(err)
      })
  }

  async function createSpouseBusiness(data, borrowerId) {
    return $axios
      .post(
        env('apiUrl') + `/borrower/${borrowerId}/new-spouse-business`,
        data
      )
      .catch((err) => {
        console.log(err)
      })
  }

  async function updateRelatedOwnerByBorrowerId(data, id) {
    return $axios
      .put(env('apiUrl') + `/contacts-family/related-owners/${id}`, data)
      .then((response) => {
        const index = relatedOwners.value.findIndex(
          (owner) => owner.ownerContactId === ownerContactId
        )
        if (index) {
          merge(relatedOwners.value[index], response.data.data)
        }
      })
      .catch((err) => {
        console.log(err)
      })
  }

  async function updateOwnerRelatedEmployeeByBorrowerId(data, id) {
    return $axios
      .put(env('apiUrl') + `/contacts-family/related-employees/${id}`, data)
      .then((response) => {
        const index = ownerRelatedEmployees.value.findIndex(
          (owner) => owner.ownerContactId === ownerContactId
        )
        if (index) {
          merge(ownerRelatedEmployees.value[index], response.data.data)
        }
      })
      .catch((err) => {
        console.log(err)
      })
  }

  async function checkIfDuplicateOwner({ first = '', last = '' }) {
    const url = `${env('apiUrl')}/contacts-family/check-duplicate`
    const response = await $axios.get(url).catch((e) => {
      console.error(e)
    })
    const duplicate = get(response, 'data.data')

    return duplicate
  }

  async function deleteErcContactById(contactId) {
    return $axios
      .delete(env('apiUrl') + `/contact/${contactId}/erc-contact`)
      .catch((err) => {
        console.log(err.response.data)
      })
  }

  async function createErcRelatedOwner(data, borrowerId) {
    return $axios
      .post(
        env('apiUrl') +
          `/contacts-family/related-owners/borrower/${borrowerId}`,
        data
      )
      .catch((err) => {
        console.log(err)
      })
  }

  async function putErcRelatedOwner(data, id) {
    return $axios
      .put(env('apiUrl') + `/contacts-family/related-owners/${id}`, data)
      .catch((err) => {
        console.log(err)
      })
  }

  async function deleteContactsFamilyById(id) {
    return $axios
      .delete(env('apiUrl') + `/contacts-family/${id}`)
      .catch((err) => {
        console.log(err)
      })
  }

  return {
    //STATE
    relatedOwners,
    relatedOwnersUnsavedAnswers,
    ownerRelatedEmployees,
    ownersLoaded,
    owners,
    primaryContactOwner,
    additionalBusinesses,
    ercContacts,
    ercContactsUnsavedAnswers,
    ownersUnsavedAnswers,
    ercSpouseOwnedBusinesses,
    ercSpouseOwnedBusinessesUnsavedAnswers,
    contactsRowTemplate:  _contactsRowTemplate,
    ownersTemplate: _ownersTemplate,
    newBusinessRowTemplate: _newBusinessRowTemplate,
    ownerRelatedEmployeesRowTemplate: _ownerRelatedEmployeesRowTemplate,
    relatedOwnersRowTemplate: _relatedOwnersRowTemplate,
    spouseOwnedBusinessesRowTemplate: _spouseOwnedBusinessesRowTemplate,
    unsavedAdditionalBusinesses,
    ownersLoaded,
    owners,
    unmodifiedOwners,
    primaryContactOwner,

    //Gettters
    hasRelatedOwners,
    hasRelatedEmployees,
    hasSpouseOwnedBusinesses,
    ercNewBusinessRows,
    ownersOptions,
    relatedOwnersOptions,
    spouseOwnedBusinessesRowTemplateWithOptions,
    ercSpouseOwnedBusinessRows,
    ercContactsRows,
    ownerRows,
    ownerRelatedEmployeesRowTemplateWithOptions,
    relatedEmployeesRows,
    relatedOwnersRowTemplateWithOptions,
    relatedOwnersRows,
    ownersRows,
    savedAndUnsavedOwners,

    //Actions
    updateUnsavedOwner,
    removeOwner,
    getRelatedOwnersByBorrowerId,
    getErcSpouseOwnedBusinesses,
    updateErcSpouseOwnedBusinesses,
    getOwnerRelatedEmployeesByBorrowerId,
    createRelatedOwnerByBorrowerId,
    createOwnerRelatedEmployeeByBorrowerId,
    getErcContactsByBorrowerId,
    createErcContact,
    updateErcContact,
    createSpouseBusiness,
    updateRelatedOwnerByBorrowerId,
    updateOwnerRelatedEmployeeByBorrowerId,
    checkIfDuplicateOwner,
    deleteErcContactById,
    createErcRelatedOwner,
    putErcRelatedOwner,
    deleteContactsFamilyById,
    getOwnersByBorrowerId,
    postOwners,
  }
})
